import React from 'react';

/**
 * EvaluationTemplateSelectBox Component
 * Reusable component for displaying evaluation templates in a selectable format
 */
const EvaluationTemplateSelectBox = ({ 
  evaluation, 
  isSelected = false, 
  onClick,
  index 
}) => {
  // Get type color based on evaluation type
  const getTypeColor = (type) => {
    switch(type) {
      case 'VSJob':
        return 'bg-purple-100 text-purple-800';
      case 'VSResume':
        return 'bg-blue-100 text-blue-800';
      case 'VSSkillset':
        return 'bg-green-100 text-green-800';
      default:
        return 'bg-gray-100 text-gray-800';
    }
  };

  return (
    <div 
      key={index} 
      className={`p-3 cursor-pointer rounded-lg border-2 ${
        isSelected 
          ? 'bg-blue-50 border-blue-400 shadow-lg ring-2 ring-blue-200' 
          : 'bg-white border-gray-200 shadow-sm hover:shadow-md hover:border-gray-300'
      } transition-all duration-200`}
      onClick={() => onClick(evaluation)}
    >
      {/* Two-column layout */}
      <div className="flex gap-4">
        {/* Left column - Main content */}
        <div className="flex-1 min-w-0">
          {/* Title and ID */}
          <div className="mb-2">
            <h3 className="font-bold text-gray-900 text-base leading-tight">
              {evaluation.name || 'Untitled Evaluation'}
            </h3>
            <p className="text-sm text-gray-500">
              {evaluation.evaluation_id ? evaluation.evaluation_id.substring(0, 12) + '...' : 'No ID'}
            </p>
          </div>
          
          {/* Badges */}
          <div className="flex items-center gap-2">
            <span className={`px-3 py-1 inline-flex text-sm font-semibold rounded-full ${getTypeColor(evaluation.type)}`}>
              {evaluation.type || 'Unknown'}
            </span>
            <span className="px-3 py-1 inline-flex text-sm font-semibold rounded-full bg-blue-100 text-blue-800">
              {evaluation.status || 'N/A'}
            </span>
          </div>
        </div>
        
        {/* Right column - Metadata and selection */}
        <div className="flex-shrink-0 text-right">
          {/* Selection indicator */}
          {isSelected && (
            <div className="mb-2 flex justify-end">
              <div className="w-6 h-6 bg-blue-600 rounded-full flex items-center justify-center">
                <svg className="w-4 h-4 text-white" fill="currentColor" viewBox="0 0 20 20">
                  <path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd" />
                </svg>
              </div>
            </div>
          )}
          
          {/* Metadata */}
          <div className="space-y-1 text-sm text-gray-600">
            <div className="flex items-center justify-end">
              <svg className="w-4 h-4 mr-1 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z" />
              </svg>
              <span>{evaluation.created_at ? new Date(evaluation.created_at).toLocaleDateString() : 'Unknown'}</span>
            </div>
            
            {evaluation.job_id && (
              <div className="flex items-center justify-end">
                <svg className="w-4 h-4 mr-1 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M21 13.255A23.931 23.931 0 0112 15c-3.183 0-6.22-.62-9-1.745M16 6V4a2 2 0 00-2-2h-4a2 2 0 00-2-2v2m8 0H8m8 0v2a2 2 0 002 2v8a2 2 0 01-2 2H8a2 2 0 01-2-2v-8a2 2 0 012-2V6" />
                </svg>
                <span className="bg-gray-100 px-2 py-1 rounded text-xs font-mono">{evaluation.job_id.substring(0, 8)}...</span>
              </div>
            )}
            
            {evaluation.talent_id && (
              <div className="flex items-center justify-end">
                <svg className="w-4 h-4 mr-1 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M16 7a4 4 0 11-8 0 4 4 0 018 0zM12 14a7 7 0 00-7 7h14a7 7 0 00-7-7z" />
                </svg>
                <span className="bg-gray-100 px-2 py-1 rounded text-xs font-mono">{evaluation.talent_id.substring(0, 8)}...</span>
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default EvaluationTemplateSelectBox;
